import {OperatorFunction} from 'rxjs';
import {concatMap} from 'rxjs/operators';

import {CancelToken} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {
  fetchRawBootAsync,
  fetchRawBootInboxesAndChannelsAsync,
} from '../../../srcCommon/services/boot/fetch/bootFetchHelpers';
import {isRawBootMetaInfo} from '../../models/api/rawBootModel';

/*
 * Types.
 */

export interface LegacyFrameRawBoot {
  isInitial: boolean;
  rawBoot: unknown;
}

/*
 * Api.
 */

export function toRawBootForLegacyFrame(companyUrl: string): OperatorFunction<LegacyFrameRawBoot, unknown> {
  return concatMap((rawBoot) => createRawBootForLegacyFrameAsync(rawBoot, companyUrl));
}

export async function fetchLegacyFrameDeltaBootAsync(
  originalRawBoot: unknown,
  companyUrl: string,
  cancelToken: CancelToken,
): Promise<unknown> {
  if (!isRawBootMetaInfo(originalRawBoot)) {
    throw new Error('originalRawBoot missing timestamp');
  }

  return fetchRawBootAsync(companyUrl, {
    deltasSince: originalRawBoot.ts,
    // We don't care enough about Old Front to deal with refresh token failures.
    refreshToken: undefined,
    cancelToken,
  });
}

/*
 * Helpers.
 */

async function createRawBootForLegacyFrameAsync(
  legacyFrameRawBoot: LegacyFrameRawBoot,
  companyUrl: string,
): Promise<unknown> {
  const rawBoot = legacyFrameRawBoot.rawBoot as any;
  if (!legacyFrameRawBoot.isInitial) {
    // The legacy frame can just take the incremental boot and patch it into its boot.
    return rawBoot;
  }

  // Old Front needs the full raw boot initially, so let's fetch and patch in inboxes/channels and teammates.
  const inboxesAndChannelsFetchResult = await fetchRawBootInboxesAndChannelsAsync(companyUrl, false, 0, null);
  const {fetchedAt: inboxesAndChannelsFetchedAt, rawInboxes, rawChannels} = inboxesAndChannelsFetchResult;

  // If we don't have lazy teammates, only patch in inboxes/channels.
  return {
    ...rawBoot,
    ts: Math.min(rawBoot.ts, inboxesAndChannelsFetchedAt),
    inboxes: rawInboxes,
    channels: rawChannels,
  };
}
